/* AI Readout (D12, 06-Oct-2026) — one card per analysis page (Correlation, Sales & Receivables,
   Trade Payables, Inventory). The readout is produced from the aggregated page results only, number-checked,
   saved per engagement and page (last 5) and marked out of date once the analysis is re-run. */
import { asArray } from "@/lib/asArray";
import React, { useCallback, useEffect, useState } from "react";
import { fmtDateTimeFirm } from "@/lib/dt";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const POLL_MS = 2000;
const UNRELIABLE = "READOUT_UNRELIABLE";

export default function ReadoutCard({ engagementId, page, analysisComputedAt }) {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [unreliable, setUnreliable] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    if (!engagementId) return;
    try {
      const r = await api.get(`/digital-audit/${engagementId}/readout/${page}`, { _silent: true });
      setData(r.data);
    } catch (e) {
      setData({ items: [] });
    }
  }, [engagementId, page]);

  useEffect(() => { load(); }, [load, analysisComputedAt]);

  const pollJob = async (jobId) => {
    let elapsed = 0;
    const MAX = 10 * 60 * 1000;
    while (elapsed < MAX) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      elapsed += POLL_MS;
      const jr = await api.get(`/jobs/${jobId}`);
      const s = jr.data?.status;
      if (s === "done") return jr.data;
      if (s === "failed") {
        const err = new Error(jobErrorText(jr.data, lang, t, t("aiReadout.failed")));
        err.unreliable = jr.data?.error_code === UNRELIABLE || (jr.data?.error || "").includes(UNRELIABLE);   // 5c · F-6
        throw err;
      }
    }
    throw new Error(t("aiReadout.failed"));
  };

  const generate = async () => {
    setRunning(true);
    setUnreliable(false);
    try {
      const r = await api.post(`/digital-audit/${engagementId}/readout/${page}`);
      await pollJob(r.data.job_id);
      await load();
      toast.success(t("aiReadout.ready"));
    } catch (e) {
      if (e?.unreliable) {
        setUnreliable(true);
      } else if (e?.response?.status !== 402) {
        toast.error(toErrMsg(e));
      }
    } finally {
      setRunning(false);
    }
  };

  const items = data?.items || [];
  const latest = items[0];
  const previous = items.slice(1);

  const renderPoints = (r) => (
    <ol className="list-decimal ps-5 space-y-2 text-sm" data-testid={`readout-points-${page}`}>
      {asArray(r.points).map((p, i) => (
        <li key={i}>
          <span>{p.finding}</span>
          {p.step && (
            <div className="text-xs text-muted-foreground mt-0.5">
              <span className="font-medium">{t("aiReadout.suggestedStep")}</span> {p.step}
            </div>
          )}
        </li>
      ))}
    </ol>
  );

  const metaLine = (r) => t("aiReadout.generatedLine")
    .replace("{when}", fmtDateTimeFirm(r.created_at))
    .replace("{who}", r.user_name || "—")
    .replace("{n}", r.figures_checked ?? 0);

  return (
    <Card data-testid={`readout-card-${page}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> {t("aiReadout.title")}
          </CardTitle>
          <div className="flex items-center gap-2">
            {previous.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setShowHistory((v) => !v)}
                      data-testid={`readout-history-${page}`}>
                <History className="h-3 w-3 me-1" />
                {t("aiReadout.previous").replace("{n}", previous.length)}
              </Button>
            )}
            <Button size="sm" variant={latest ? "outline" : "default"} onClick={generate} disabled={running}
                    data-testid={`readout-generate-${page}`}>
              {running
                ? <Loader2 className="h-3 w-3 me-1 animate-spin" />
                : (latest ? <RotateCcw className="h-3 w-3 me-1" /> : <Sparkles className="h-3 w-3 me-1" />)}
              {running ? t("aiReadout.generating") : (latest ? t("aiReadout.regenerate") : t("aiReadout.button"))}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {unreliable && (
          <div className="text-sm text-destructive" data-testid={`readout-unreliable-${page}`}>
            {t("aiReadout.unreliable")}
          </div>
        )}
        {!latest && !unreliable && (
          <div className="text-sm text-muted-foreground">{t("aiReadout.empty")}</div>
        )}
        {latest && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              <span data-testid={`readout-meta-${page}`}>{metaLine(latest)}</span>
              {latest.out_of_date && (
                <Badge variant="destructive" data-testid={`readout-outdated-${page}`}>
                  {t("aiReadout.outOfDate")}
                </Badge>
              )}
            </div>
            {renderPoints(latest)}
          </div>
        )}
        {showHistory && previous.map((r) => (
          <div key={r.id} className="border-t pt-2 space-y-1 opacity-80">
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              <span>{metaLine(r)}</span>
              {r.out_of_date && <Badge variant="outline">{t("aiReadout.outOfDate")}</Badge>}
            </div>
            {renderPoints(r)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
